"use client";

import Link from "next/link";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { trackClientEvent } from "@/lib/analyticsClient";
import { trackLeadPixel } from "@/lib/facebookClient";
import { TrialCTA } from "@/components/marketing/trial-cta";

export interface PainPointsProps {
  // `"start"` mirrors the /start LP hero CTAs: "Try Sandbox" + "Start 7-Days for $1".
  variant?: "default" | "start";
  ctaLocation?: string;
}

const painPoints = [
  {
    text: "Guessing strikes inside their broker",
    description:
      "No screener, no data - just scrolling option chains and hoping for the best.",
  },
  {
    text: "Tracking rolls in messy spreadsheets",
    description:
      "Manual logging that breaks the moment you roll, get assigned, or adjust.",
  },
  {
    text: "Forgetting the real cost basis after adjustments",
    description:
      "Rolls, assignments, and splits make it impossible to know your true P&L.",
  },
  {
    text: "Checking 3\u20134 tools just to make one decision",
    description:
      "Broker, screener, spreadsheet, Discord - context-switching kills your edge.",
  },
];

export default function PainPoints({
  variant = "default",
  ctaLocation = "pain_points_section",
}: PainPointsProps = {}) {
  const isStart = variant === "start";
  return (
    <section className="relative overflow-hidden border-y border-white/[0.04] bg-gradient-to-b from-[#0c0c0d] via-[#0e0e10] to-[#0c0c0d] py-14 md:py-20">
      <MaxWidthWrapper className="relative z-10 px-4 sm:px-6">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          {isStart ? (
            <h2 className="text-3xl tracking-tight text-white md:text-4xl lg:text-[42px] lg:leading-[1.2]">
              Wheel trading gets messy when your tools{" "}
              <span className="text-gradient_green">
                do not talk to each other.
              </span>
            </h2>
          ) : (
            <h2 className="text-3xl tracking-tight text-white md:text-4xl lg:text-[42px] lg:leading-[1.2]">
              The biggest risk isn&apos;t assignment.{" "}
              <br />
              <span className="text-gradient_green">
                It&apos;s running the Wheel without a&nbsp;system.
              </span>
            </h2>
          )}
          <p className="mt-5 text-base text-white/50 sm:text-lg">
            Most traders today are:
          </p>
        </div>

        {/* Pain points - hairline editorial rows, no cards or icon boxes. */}
        <div className="relative mx-auto mt-12 max-w-3xl">
          <div
            className="pointer-events-none absolute -inset-x-24 inset-y-0 rounded-full bg-red-500/[0.05] blur-3xl"
            aria-hidden="true"
          />
          <div className="relative border-b border-white/[0.07]">
            {painPoints.map((point, i) => (
              <div
                key={point.text}
                className="group grid gap-1.5 border-t border-white/[0.07] py-6 text-left sm:grid-cols-[3.5rem_1fr] sm:gap-6"
              >
                <span className="pt-0.5 font-mono text-sm tabular-nums text-red-400/50 transition-colors group-hover:text-red-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className="text-[15px] font-medium leading-snug text-white/85 transition-colors group-hover:text-white">
                    {point.text}
                  </p>
                  <p className="mt-1.5 text-sm leading-relaxed text-white/40">
                    {point.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Yield cost + CTA */}
        <div className="mx-auto mt-10 max-w-2xl text-center">
          <p className="text-sm text-white/35">
            That friction adds up. You lose hours every week, and you miss good trades.
          </p>
          {isStart ? (
            <p className="mt-6 text-lg text-white/60 md:text-xl">
              <span className="text-gradient_green">
                QuantWheel puts the wheel process in one clean place
              </span>{" "}
              so you can inspect the trade before you act.
            </p>
          ) : (
            <p className="mt-6 text-lg text-white/60 md:text-xl">
              <span className="text-gradient_green">
                QuantWheel replaces the chaos with one clean workflow
              </span>{" "}
              for the entire Wheel strategy in under 20&nbsp;minutes.
            </p>
          )}

          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            {isStart ? (
              <div className="flex flex-col items-center gap-3">
                <TrialCTA direct ctaLocation={ctaLocation} className="h-12 px-8 text-lg">
                  Start my $1 trial
                </TrialCTA>
                <Link
                  href="/dashboard/wheel"
                  className="text-xs text-white/50 underline underline-offset-4 transition-colors hover:text-white/80 sm:text-sm"
                  onClick={() => {
                    trackClientEvent({
                      name: "sandbox_cta_clicked",
                      params: { cta_location: ctaLocation },
                    });
                  }}
                >
                  Just exploring? Explore the free Sandbox with demo data.
                </Link>
              </div>
            ) : (
              <>
                <TrialCTA ctaLocation={ctaLocation} />
                <Link
                  href="/dashboard/wheel/find-deals"
                  className={cn(
                    buttonVariants({
                      variant: "outline",
                      size: "lg",
                      rounded: "full",
                    }),
                    "border-white/20 px-6 text-base text-white backdrop-blur-sm hover:border-white/40 hover:bg-white/10",
                  )}
                  onClick={() => {
                    trackLeadPixel("Pain Points CTA - Open Dashboard");
                    trackClientEvent({
                      name: "pain_points_cta_clicked",
                      params: {
                        cta_text: "Open Dashboard",
                        cta_location: ctaLocation,
                      },
                    });
                  }}
                >
                  Open Dashboard
                </Link>
              </>
            )}
          </div>
        </div>
      </MaxWidthWrapper>
    </section>
  );
}
